/**
 * Add / edit one allergy. Extracted verbatim from AllergiesPage so the VisitStart
 * check-in asks for an allergy the same way the record page does, instead of
 * shipping its own thinner form: allergen with RxNorm lookup for drugs, the type
 * select, the common-allergen shortcuts, reaction and notes.
 *
 * Owns the form and nothing else. `onSave` receives the allergy and the caller
 * decides where it goes — the record page writes it to `healthRecords.allergies`,
 * the check-in holds it in the list for this visit until the patient confirms.
 * The dialog closes itself once onSave resolves, and shows an error in place if it
 * rejects, which is the same contract MedicationForm already has.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useAsyncAutocomplete } from "hooks/useAsyncAutocomplete";
import { rxTermsService } from "services/rxTermsService";

export const ALLERGY_TYPES = [
  { value: "drug", label: "Drug / Medication" },
  { value: "food", label: "Food" },
  { value: "environmental", label: "Environmental" },
  { value: "other", label: "Other" },
];

export const COMMON_ALLERGENS = {
  drug: [
    "Penicillin",
    "Sulfa Drugs",
    "Aspirin",
    "NSAIDs",
    "Codeine",
    "Morphine",
    "Iodine Contrast",
    "Latex",
  ],
  food: ["Peanuts", "Tree Nuts", "Shellfish", "Fish", "Eggs", "Milk", "Soy", "Wheat"],
  environmental: ["Pollen", "Dust Mites", "Pet Dander", "Mold", "Bee Stings", "Latex"],
  other: [],
};

export const allergyTypeLabel = (type) =>
  ALLERGY_TYPES.find((t) => t.value === type)?.label ?? type;

const EMPTY = { allergen: "", type: "drug", reaction: "", notes: "" };

/**
 * `essentialOnly` is for the VisitStart check-in, and it drops only the notes
 * field. This form is already close to the bone and the rest of it earns its
 * place at intake: the type select is what routes a drug allergen through the
 * RxNorm lookup that the interaction check depends on, the common-allergen chips
 * are one-tap entry rather than friction, and "Penicillin — anaphylaxis" versus
 * "Penicillin — mild rash" is the difference the prescriber acts on. Free-text
 * notes are the one part a patient cannot usefully fill in standing up.
 */
export default function AllergyForm({
  open,
  onClose,
  onSave,
  allergy = null,
  isEdit = false,
  essentialOnly = false,
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedRxcui, setSelectedRxcui] = useState("");

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: EMPTY,
  });

  const watchedType = watch("type");
  const watchedAllergen = watch("allergen");

  const searchDrugs = useCallback((q) => rxTermsService.searchMedications(q, 15), []);
  const { options: drugOptions, loading: drugLoading } = useAsyncAutocomplete(
    watchedType === "drug" ? watchedAllergen : "",
    searchDrugs,
    { minLength: 2 },
  );

  // Reset from the record being edited each time the dialog opens, same as
  // MedicationForm — a stale value from the last edit is a wrong allergy.
  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    reset({
      allergen: allergy?.allergen ?? "",
      type: allergy?.type ?? "drug",
      reaction: allergy?.reaction ?? "",
      notes: allergy?.notes ?? "",
    });
    setSelectedRxcui(allergy?.rxcui ?? "");
  }, [open, allergy, reset]);

  const onSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        allergen: data.allergen.trim(),
        type: data.type,
        reaction: data.reaction.trim(),
        // Omitted rather than stored blank, which is all it can be once the
        // check-in hides the field.
        ...(data.notes.trim() ? { notes: data.notes.trim() } : {}),
        ...(data.type === "drug" && selectedRxcui ? { rxcui: selectedRxcui } : {}),
      });
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{isEdit ? "Edit Allergy" : "Add Allergy"}</DialogTitle>
      <DialogContent>
        {/* pt clears the first field's floating label, which sits above its own
            border and would otherwise be sliced: MUI zeroes a DialogContent's
            top padding when it follows a DialogTitle, so the scroll box starts
            flush with the label. 8px was not enough for it. */}
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}

          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="allergy-type-label">Allergy Type</InputLabel>
                <Select
                  {...field}
                  labelId="allergy-type-label"
                  label="Allergy Type"
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("allergen", "");
                    setSelectedRxcui("");
                  }}
                >
                  {ALLERGY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {watchedType === "drug" ? (
            <Controller
              name="allergen"
              control={control}
              rules={{ required: "Allergen is required" }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <Autocomplete
                  freeSolo
                  options={drugOptions}
                  loading={drugLoading}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt?.name ?? opt?.displayName ?? ""
                  }
                  inputValue={field.value || ""}
                  onInputChange={(_, value, reason) => {
                    if (reason !== "reset") {
                      field.onChange(value);
                      if (reason === "clear" || reason === "input") setSelectedRxcui("");
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) return;
                    if (typeof newValue === "string") {
                      field.onChange(newValue);
                      setSelectedRxcui("");
                    } else {
                      field.onChange(newValue.name || newValue.displayName || "");
                      setSelectedRxcui(newValue.rxcui ? String(newValue.rxcui) : "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Drug / Allergen"
                      placeholder="e.g., Penicillin"
                      autoFocus
                      error={!!fieldError}
                      helperText={
                        fieldError?.message ??
                        "Standard drugs support drug-allergy interaction checks."
                      }
                      inputProps={{
                        ...params.inputProps,
                        "data-testid": "allergy-allergen",
                      }}
                    />
                  )}
                />
              )}
            />
          ) : (
            <Controller
              name="allergen"
              control={control}
              rules={{ required: "Allergen is required" }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  {...field}
                  label="Allergen"
                  fullWidth
                  autoFocus
                  placeholder={watchedType === "food" ? "e.g., Peanuts" : "e.g., Pollen"}
                  error={!!fieldError}
                  helperText={fieldError?.message}
                  inputProps={{ "data-testid": "allergy-allergen" }}
                />
              )}
            />
          )}

          {(COMMON_ALLERGENS[watchedType]?.length ?? 0) > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Common allergens:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {COMMON_ALLERGENS[watchedType].map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setValue("allergen", name);
                      setSelectedRxcui("");
                    }}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Controller
            name="reaction"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Reaction"
                fullWidth
                placeholder="e.g., Hives, Anaphylaxis, Rash"
                inputProps={{ "data-testid": "allergy-reaction" }}
              />
            )}
          />

          {!essentialOnly && (
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Additional details (optional)"
                />
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={saving}
          disabled={saving}
          data-testid="allergy-save"
        >
          {isEdit ? "Update" : "Add"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AllergyForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Receives the allergy. Reject to keep the dialog open with an error. */
  onSave: PropTypes.func.isRequired,
  /** The record being edited, or null to add. */
  allergy: PropTypes.object,
  isEdit: PropTypes.bool,
  /** Drop the notes field. See the note above the component. */
  essentialOnly: PropTypes.bool,
};
